<!--
// app/components/pages/region-tier-list/LayoutTierListKwcClubs.vue
-->
<script setup lang="ts">
import type { TierListTeam } from "~~/src/tier-list/domain/models/TierList";
import type { SeasonDTO } from "~~/src/season-cluster/infrastructure/DTO/01.SeasonDTO";
import draggable from "vuedraggable";
import LineTierDraggableKwc from "~/components/draggable/LineTierDraggableKwc.vue";
import ResetIcon from "~/components/Shared/icons/ResetIcon.vue";
import DownloadIcon from "~/components/Shared/icons/DownloadIcon.vue";
import { setAliasUrl } from "~~/src/Shared/utils";

type Tier = {
  id: string;
  label: string;
  color: string;
  teams: TierListTeam[];
};

const { $t } = useI18n();

const pageIsActive = true;

const KWCLogoDate = "/img/world-cup/kwc-clubs-horizontal-2026.png";
const KWCWebText = "/img/world-cup/kwc-web-text.png";

const createDefaultTiers = (): Tier[] => [
  { id: "s", label: "S", color: "#FF7F7F", teams: [] },
  { id: "a", label: "A", color: "#FFBF7F", teams: [] },
  { id: "b", label: "B", color: "#FFFF7F", teams: [] },
  { id: "c", label: "C", color: "#7FFF7F", teams: [] },
  { id: "d", label: "D", color: "#7FBFFF", teams: [] },
];

const tiers = ref<Tier[]>(createDefaultTiers());
const poolItems = ref<TierListTeam[]>([]);

const drag = ref(false);

const { data: seasonData, pending: loadingTeams } = useLazyFetch<SeasonDTO>(
  "/api/v1/competition/seasons/64/teams"
);

function teamsFromSeason(season: SeasonDTO | null | undefined): TierListTeam[] {
  return (season?.teams ?? []).map(team => ({
    id: team.id,
    logo: team.logo?.url ?? "",
    name: team.name,
    kwcGroup: "pool",
  }));
}

watch(
  seasonData,
  season => {
    poolItems.value = teamsFromSeason(season);
  },
  { immediate: true }
);

const allDraggedTeams = computed(() => tiers.value.flatMap(t => t.teams));

const resetTierList = () => {
  poolItems.value = teamsFromSeason(seasonData.value);
  tiers.value = createDefaultTiers();
};

const isDownloading = ref(false);
const showDownloadModal = ref(false);
const showShareModal = ref(false);

const canNativeShare = computed(() => {
  if (typeof navigator === "undefined") return false;
  return !!navigator.share && !!navigator.canShare;
});

async function generateImage(): Promise<string | null> {
  if (typeof window === "undefined" || !(window as any).domtoimage) return null;
  const el = document.getElementById("TierList") as HTMLElement | null;
  if (!el) return null;

  const imgs = Array.from(el.querySelectorAll<HTMLImageElement>("img"));
  await Promise.allSettled(
    imgs.map(img =>
      img.complete
        ? Promise.resolve()
        : new Promise<void>(resolve => {
            img.onload = () => resolve();
            img.onerror = () => resolve();
          })
    )
  );

  const restorers: Array<() => void> = [];
  for (const img of imgs) {
    if (!img.complete || !img.naturalWidth) continue;
    const computedStyle = window.getComputedStyle(img);
    if (computedStyle.objectFit === "cover") continue;
    try {
      const canvas = document.createElement("canvas");
      const w = img.offsetWidth || img.naturalWidth;
      const h = img.offsetHeight || img.naturalHeight;
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (!ctx) continue;
      ctx.drawImage(img, 0, 0, w, h);
      const origSrc = img.getAttribute("src") ?? "";
      const origSrcset = img.getAttribute("srcset") ?? "";
      img.setAttribute("src", canvas.toDataURL("image/png"));
      img.removeAttribute("srcset");
      restorers.push(() => {
        img.setAttribute("src", origSrc);
        if (origSrcset) img.setAttribute("srcset", origSrcset);
      });
    } catch (e) {
      console.warn("Could not inline image for download:", e);
    }
  }

  try {
    const dataUrl = await (window as any).domtoimage.toPng(el, { quality: 1, bgcolor: "#000" });
    return dataUrl;
  } catch (error) {
    console.error("Download error:", error);
    return null;
  } finally {
    el.style.cssText = "";
    restorers.forEach(fn => fn());
  }
}

function downloadDataUrl(dataUrl: string) {
  const link = document.createElement("a");
  link.href = dataUrl;
  link.download = "tierKwc.png";
  link.click();
  link.remove();
}

function dataUrlToBlob(dataUrl: string): Blob {
  const [header, data] = dataUrl.split(",");
  const mime = header.match(/:(.*?);/)?.[1] ?? "image/png";
  const binary = atob(data);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

const capture = async () => {
  if (typeof window === "undefined" || !(window as any).domtoimage) return;

  isDownloading.value = true;
  await nextTick();
  await new Promise<void>(resolve => setTimeout(resolve, 200));

  const dataUrl = await generateImage();
  isDownloading.value = false;

  if (!dataUrl) return;

  downloadDataUrl(dataUrl);
  showDownloadModal.value = false;

  if (canNativeShare.value) {
    try {
      const blob = dataUrlToBlob(dataUrl);
      const file = new File([blob], "tierKwc.png", { type: "image/png" });
      if (navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: "KWC Tier List",
          text: "#KingsWorldCupClubs",
        });
        return;
      }
    } catch (e: any) {
      if (e?.name === "AbortError") return;
      // Share failed for other reason — fall through to modal
    }
  }

  showShareModal.value = true;
};

const shareOnTwitter = () => {
  const text = encodeURIComponent("#KingsWorldCupClubs");
  window.open(`https://twitter.com/intent/tweet?text=${text}`, "_blank", "noopener");
};

const shareOnInstagram = () => {
  window.open("https://www.instagram.com/", "_blank", "noopener");
};

const shareOnTikTok = () => {
  window.open("https://www.tiktok.com/upload", "_blank", "noopener");
};

const shareOnWhatsApp = () => {
  const text = encodeURIComponent("#KingsWorldCupClubs");
  window.open(`https://wa.me/?text=${text}`, "_blank", "noopener");
};

useSeoMeta({
  title: $t("worldCup.tierList.title", { kwcType: "Clubs" }),
  ogTitle: $t("worldCup.tierList.ogTitle", { kwcType: "Clubs" }),
  ogDescription: $t("worldCup.tierList.description"),
  description: $t("worldCup.tierList.description"),
});
</script>

<template>
  <section class="pb-6 min-w-full min-h-full main-container">
    <div
      class="flex flex-col bigsm:flex-row justify-start items-center gap-3 bigsm:gap-9 px-4 pt-4 w-full max-w-full flex-wrap"
    >
      <img
        :src="KWCLogoDate"
        alt="Kings World Cup"
        width="229px"
        height="auto"
        class="w-[150px] md:w-[190px]"
      />
      <div class="text-center bigsm:text-left text-base bigsm:text-xl">
        <div class="font-black md:flex md:gap-2">
          <p>{{ $t("worldCup.tierList.predictions.text") }}</p>
        </div>
        <p v-if="pageIsActive">{{ $t("worldCup.tierList.predictions.download") }}</p>
      </div>

      <div class="flex items-center gap-4 flex-1">
        <button
          v-if="pageIsActive"
          class="btn btn-default flex items-center justify-center lg:ms-auto text-sm"
          @click="resetTierList"
        >
          <ResetIcon />
          {{ $t("worldCup.tierList.button.restart") }}
        </button>
        <button
          v-if="pageIsActive"
          :disabled="allDraggedTeams.length === 0"
          class="btn btn-emphasis flex items-center justify-center disabled:opacity-70 text-sm leading-none bigsm:whitespace-nowrap"
          @click="showDownloadModal = true"
        >
          <DownloadIcon />
          {{ $t("worldCup.tierList.button.downloadAndShare") }}
        </button>
      </div>
    </div>

    <div class="kql-w-container px-3.5 md:px-6 py-4 mt-6 lg:mt-10">
      <div class="kwc-tier-container">
        <draggable
          v-model="tiers"
          item-key="id"
          handle=".drag-row-handle"
          @start="drag = true"
          @end="drag = false"
          ghost-class="row-draggable-ghost"
          animation="200"
        >
          <template #item="{ element: tier }">
            <LineTierDraggableKwc v-model:label="tier.label" :accent-color="tier.color">
              <draggable
                v-model="tier.teams"
                item-key="id"
                @start="drag = true"
                @end="drag = false"
                ghost-class="item-draggable-ghost"
                :group="{
                  name: 'teams',
                  pull: true,
                  put: true,
                }"
                class="tier-teams-container"
              >
                <template #item="{ element }">
                  <NuxtImg
                    v-tooltip.top="element.name"
                    :src="setAliasUrl(element.logo)"
                    :alt="element.name"
                    :data-kwc="element.kwcGroup"
                    draggable="false"
                    class="assigned-team-logo"
                  />
                </template>
              </draggable>
            </LineTierDraggableKwc>
          </template>
        </draggable>
      </div>
    </div>

    <div class="kql-w-container max-w-full py-6">
      <div v-if="pageIsActive">
        <div v-if="loadingTeams" class="flex justify-center items-center py-8">
          <svg class="w-8 h-8 animate-spin text-primary" viewBox="0 0 24 24" fill="none">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" />
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        </div>
        <draggable
          v-else
          v-model="poolItems"
          item-key="id"
          @start="drag = true"
          @end="drag = false"
          ghost-class="item-draggable-ghost"
          :group="{
            name: 'pool',
            pull: true,
            put: (to, from, evt) => evt.dataset.kwc === 'pool',
          }"
          :sort="false"
          class="flex flex-wrap justify-center gap-2 px-2"
        >
          <template #item="{ element }">
            <NuxtImg
              v-tooltip.top="element.name"
              :src="setAliasUrl(element.logo)"
              :alt="element.name"
              draggable="false"
              :data-kwc="element.kwcGroup"
              class="pending-assign-team-logo"
            />
          </template>
          <template #footer>
            <NuxtImg
              v-for="team in allDraggedTeams"
              :key="team.id"
              :src="setAliasUrl(team.logo)"
              :alt="team.name"
              draggable="false"
              class="ghost-team-logo"
            />
          </template>
        </draggable>
      </div>
      <div v-else class="text-center italic">{{ $t("worldCup.tierList.noTeams") }}</div>
    </div>
  </section>

  <BaseModal
    is-preview
    modal-id="download-tier-list-modal"
    :model-value="showDownloadModal"
    @update:model-value="showDownloadModal = $event"
  >
    <template #body>
      <div
        class="overflow-hidden mx-auto max-w-full"
        style="width: 400px; height: 220px; display: flex; justify-content: center; align-items: flex-start"
      >
        <div style="transform: scale(0.3); transform-origin: top center">
          <section
            id="TierList"
            class="pt-6 px-6 pb-10 w-[1100px] h-[600px] bg-[url('/img/world-cup/background_1100_tier-list.svg')] bg-cover bg-no-repeat bg-left-top"
          >
            <div>
              <div class="flex justify-between items-center gap-3 px-4 pt-4 pb-6 w-full max-w-full">
                <img
                  :src="KWCLogoDate"
                  alt="Kings World Cup Clubs"
                  width="150px"
                  height="auto"
                  class="w-[180px]"
                />
                <img
                  :src="KWCWebText"
                  alt="kingsleague.pro/kwc"
                  width="230px"
                  height="auto"
                  class="w-[230px]"
                />
              </div>
              <div class="kwc-tier-container">
                <LineTierDraggableKwc
                  v-for="tier in tiers"
                  :key="tier.id"
                  :label="tier.label"
                  :accent-color="tier.color"
                  is-canvas-image
                >
                  <div class="tier-teams-container-static">
                    <NuxtImg
                      v-for="element in tier.teams"
                      :key="element.id"
                      :src="setAliasUrl(element.logo)"
                      :alt="element.name"
                      class="w-[60px] h-[60px] object-contain object-center"
                    />
                  </div>
                </LineTierDraggableKwc>
              </div>
            </div>
          </section>
        </div>
      </div>
    </template>
    <template #footer>
      <div class="flex items-center justify-evenly gap-2">
        <button class="btn btn-default" @click="showDownloadModal = false">
          {{ $t("squadSimulator.button.cancel") }}
        </button>
        <button
          class="btn flex items-center btn-emphasis justify-center gap-2"
          :disabled="isDownloading"
          @click="capture"
        >
          <BaseSpinner v-if="isDownloading" class="!fill-black" :size="25" />
          {{ $t("worldCup.tierList.button.downloadAndShare") }}
        </button>
      </div>
    </template>
  </BaseModal>

  <BaseModal
    modal-id="share-tier-list-modal"
    :model-value="showShareModal"
    @update:model-value="showShareModal = $event"
  >
    <template #body>
      <div class="flex flex-col items-center gap-4 py-4 px-2 text-center">
        <p class="font-bold text-lg">{{ $t("worldCup.tierList.share.title") }}</p>
        <p class="text-sm text-gray-400">{{ $t("worldCup.tierList.share.description") }}</p>
        <div class="flex gap-6 mt-2">
          <button
            v-tooltip.top="$t('worldCup.tierList.share.networks.twitter')"
            class="share-icon-btn"
            @click="shareOnTwitter"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path
                d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.736-8.857L1.254 2.25H8.08l4.253 5.622 5.911-5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"
              />
            </svg>
          </button>
          <button
            v-tooltip.top="$t('worldCup.tierList.share.networks.instagram')"
            class="share-icon-btn"
            @click="shareOnInstagram"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path
                d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"
              />
            </svg>
          </button>
          <button
            v-tooltip.top="$t('worldCup.tierList.share.networks.tiktok')"
            class="share-icon-btn"
            @click="shareOnTikTok"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path
                d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.18 8.18 0 004.78 1.52V6.76a4.85 4.85 0 01-1.01-.07z"
              />
            </svg>
          </button>
          <button
            v-tooltip.top="$t('worldCup.tierList.share.networks.whatsapp')"
            class="share-icon-btn"
            @click="shareOnWhatsApp"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path
                d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"
              />
            </svg>
          </button>
        </div>
      </div>
    </template>
  </BaseModal>
</template>

<style scoped>
.tier-teams-container {
  @apply flex-1 bg-gray-900 min-h-[80px] flex flex-wrap items-center gap-x-2.5 md:gap-x-4 gap-y-2 px-3 py-2;
}

.tier-teams-container-static {
  @apply flex-1 bg-gray-900 min-h-[80px] flex flex-wrap items-center gap-x-4 gap-y-2 px-3 py-2;
}

.assigned-team-logo {
  @apply w-[60px] h-[60px] object-contain object-center cursor-pointer;
}

.ghost-team-logo {
  @apply w-[50px] h-[50px] md:w-[60px] md:h-[60px] object-contain object-center opacity-30 cursor-none;
}

.pending-assign-team-logo {
  @apply w-[50px] h-[50px] md:w-[60px] md:h-[60px] object-contain object-center cursor-pointer;
}

.main-container {
  background-image: url("/img/world-cup/bg-tournament-kwc.jpg");
  min-height: calc(100svh - 20svh);
  background-size: cover;
  background-position: top center;
  background-repeat: no-repeat;
}

.kwc-tier-container {
  @apply w-full rounded-lg overflow-x-auto overflow-y-hidden;
  border: 1px solid #d4b78e;
}

.kwc-tier-container > * {
  min-width: 480px;
}

.share-icon-btn {
  @apply flex items-center justify-center w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 transition-colors text-white;
}

.share-icon-btn svg {
  @apply w-6 h-6;
}
</style>
